import React from 'react'
import {Link as RouteLink} from 'react-router-dom'
import PropTypes from 'prop-types'
import {Text, Link, useBreakpointValue} from '@chakra-ui/react'
import usePDCResponsiveElement from '../../../hooks/use-pdc-responsive-element.js'
import {DYNAMIC_IMAGE_WIDTHS} from '../../../constants.js'
import {useUrlFromLink} from '../../helpers/urlHelper'
import PDCImage from '../../../components/pdc-image/index.jsx'

/**
 * Renders the PDCAboutIconicBlockTile component, a responsive and accessible tile used to display an iconic block with an image, title, and description.
 *
 * This component dynamically selects the appropriate image based on the current viewport breakpoint and the number of slides to ensure optimal display across devices. It supports both internal and external links, and allows for customization of link behavior, text color, and image attributes. The tile displays a title and an optional description, both of which support HTML content.
 *
 * Props:
 * @param {string} title - The main title text for the tile, rendered as HTML.
 * @param {string} desc - Optional description text for the tile, rendered as HTML.
 * @param {string} textColor - The color to apply to the text content.
 * @param {string} link - The default URL to navigate to when the tile is clicked.
 * @param {string} smallImage - Image source for small breakpoints.
 * @param {string} mediumImage - Image source for medium breakpoints.
 * @param {string} largeImage - Image source for large breakpoints.
 * @param {string} xlargeImage - Image source for extra-large breakpoints.
 * @param {string} xxlargeImage - Image source for double extra-large breakpoints.
 * @param {string} imageTitle - The title attribute for the image, used for accessibility.
 * @param {string} imageAlt - The alt attribute for the image, used for accessibility.
 * @param {number} displayPriority - Priority for image loading/display.
 * @param {number} nbSlides - Number of slides in the carousel, used to determine image sizing.
 * @param {object} custom - Optional custom configuration object, may contain a custom link and link type.
 *
 * @returns {JSX.Element} A styled, responsive tile component containing an image, title, and optional description, wrapped in a link.
 */
export const PDCAboutIconicBlockTile = ({
    title,
    desc,
    textColor,
    link,
    smallImage,
    mediumImage,
    largeImage,
    xlargeImage,
    xxlargeImage,
    imageTitle,
    imageAlt,
    displayPriority,
    nbSlides,
    custom
}) => {
    // Get current image based on breakpoint
    const currentImage = useBreakpointValue(
        usePDCResponsiveElement({
            smallEl: smallImage,
            mediumEl: mediumImage,
            largeEl: largeImage,
            xlargeEl: xlargeImage,
            xxlargeEl: xxlargeImage
        }),
        {
            fallback: undefined
        }
    )

    const updatedUrl = useUrlFromLink(custom?.link) || link
    const isLinkExternal = custom?.link?.type === 'EXTERNAL'

    let imageWidths
    switch (nbSlides) {
        case 1:
            imageWidths = DYNAMIC_IMAGE_WIDTHS.full
            break
        case 2:
            imageWidths = DYNAMIC_IMAGE_WIDTHS.halfandfullonsmalldevice
            break
        case 3:
            imageWidths = DYNAMIC_IMAGE_WIDTHS.thirdandfullonsmalldevice
            break
        case 4:
            imageWidths = DYNAMIC_IMAGE_WIDTHS.quarterandfullonsmalldevice
            break
        default:
            imageWidths = DYNAMIC_IMAGE_WIDTHS.halfandfullonsmalldevice
            break
    }

    return (
        <Link
            as={isLinkExternal ? Link : RouteLink}
            href={updatedUrl}
            to={updatedUrl}
            display="flex"
            flexDirection={'column'}
            justifyContent={'center'}
            alignItems={'flex-start'}
            textAlign={'left'}
            textDecoration="none"
            p={[22, null, null, 30]}
            color={textColor}
        >
            <PDCImage
                currentImage={currentImage}
                imageWidths={imageWidths}
                imageAlt={imageAlt}
                imageTitle={imageTitle}
                displayPriority={displayPriority}
                title={title}
                skeletonProps={{aspectRatio: '57 / 70', width: '100%'}}
                width="100%"
            />

            <Text
                variant="eyeBrowLarge"
                mt={12}
                dangerouslySetInnerHTML={{
                    __html: title
                }}
            />
            {desc && (
                <Text
                    mt={10}
                    variant="bodySmall"
                    dangerouslySetInnerHTML={{
                        __html: desc
                    }}
                />
            )}
        </Link>
    )
}

PDCAboutIconicBlockTile.propTypes = {
    title: PropTypes.string.isRequired,
    desc: PropTypes.string,
    textColor: PropTypes.string,
    link: PropTypes.string,
    linkLabel: PropTypes.string,
    smallImage: PropTypes.object.isRequired,
    mediumImage: PropTypes.object,
    largeImage: PropTypes.object,
    xlargeImage: PropTypes.object,
    xxlargeImage: PropTypes.object,
    imageTitle: PropTypes.string,
    imageAlt: PropTypes.string,
    displayPriority: PropTypes.bool,
    nbSlides: PropTypes.number,
    custom: PropTypes.shape({
        link: PropTypes.shape({
            target: PropTypes.string,
            type: PropTypes.string
        })
    })
}
